import {
  IGetRecordResponse,
  IPracticeMigration,
  type ITranslationMap,
} from '@principle-theorem/practice-migrations/interfaces';
import { PracticeWorksTransaction } from '../../../source/entities/patient-transactions';
import { DocumentReference } from '@google-cloud/firestore';
import {
  type ITransaction,
  type IStaffer,
  TransactionProvider,
  TransactionType,
  IManualExtendedData,
  IPractice,
  TransactionStatus,
  IDiscountExtendedData,
  IBasePatient,
  IPatientContactDetails,
  type IInvoice,
  IAccountDetails,
  IPatient,
  ITreatmentLineItem,
  ICustomLineItem,
  ITreatmentConfiguration,
  IManualTransactionType,
  InvoiceLineItemType,
  InvoiceStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  isINamedDocument,
  isSameRef,
  resolveSequentially,
  sortByCreatedAt,
  sortTimestampAsc,
  toMomentTz,
  toNamedDocument,
  toTimestamp,
  WithRef,
  type IIdentifiable,
  type INamedDocument,
  type WithPrimaryKey,
} from '@principle-theorem/shared';
import { getPractitionerOrDefault } from '../../../../mappings/staff';
import { ItemCodeResourceMapType } from '../../../../mappings/item-codes-to-xlsx';
import { Money, TaxStrategy } from '@principle-theorem/accounting';
import {
  getTransactionAmount,
  Invoice,
  toAccountDetails,
  Transaction,
  TransactionOperators,
} from '@principle-theorem/principle-core';
import { groupBy, sortBy, sum, uniqBy } from 'lodash';
import { TranslationMapHandler } from '../../../../translation-map';
import { PATIENT_RESOURCE_TYPE } from '../../../../destination/entities/patient';
import { resolveMappedCodeTreatmentPair } from '../../../../mappings/item-codes';
import { buildExtendedData } from '../../../../destination/entities/patient-invoices';
import { getTransactionsForBalance } from '../../../../oasis/destination/entities/lib/oasis-invoice-builder';

interface IInvoiceBuildData {
  invoice: IIdentifiable & IInvoice;
  transactions: PractitionerTransaction[];
  practitionerRef: DocumentReference<IStaffer>;
}

interface IInvoiceDataWithRemainder {
  invoices: IInvoiceBuildData[];
  remainingTransactions: PractitionerTransaction[];
}

type PractitionerTransaction = IIdentifiable &
  ITransaction & { practitionerRef: DocumentReference<IStaffer> };

interface IAccountSummary {
  balance: number;
  sourceLineItems: IGetRecordResponse<PracticeWorksTransaction>[];
  sourceTransactions: IGetRecordResponse<PracticeWorksTransaction>[];
  sourceRefunds: IGetRecordResponse<PracticeWorksTransaction>[];
  sourceDiscounts: IGetRecordResponse<PracticeWorksTransaction>[];
  transactions: PractitionerTransaction[];
  refunds: PractitionerTransaction[];
  discounts: PractitionerTransaction[];
}

export class PracticeWorksInvoiceBuilder {
  accountSummary: IAccountSummary = {
    balance: 0,
    sourceTransactions: [],
    sourceLineItems: [],
    sourceRefunds: [],
    sourceDiscounts: [],
    transactions: [],
    refunds: [],
    discounts: [],
  };

  constructor(
    public migration: WithRef<IPracticeMigration>,
    public translationMap: TranslationMapHandler,
    public staff: WithPrimaryKey<ITranslationMap<IStaffer, unknown>>[],
    public practitioners: INamedDocument<IStaffer>[],
    public sourceItemCodes: WithPrimaryKey<
      ITranslationMap<object, ItemCodeResourceMapType>
    >[],
    public sourceTreatmentMappings: WithPrimaryKey<
      ITranslationMap<ITreatmentConfiguration>
    >[],
    public transactionTypes: WithPrimaryKey<
      ITranslationMap<object, TransactionProvider>
    >[]
  ) {}

  buildAccountSummary(
    builderData: Omit<IAccountSummary, 'transactions' | 'refunds' | 'discounts'>
  ): void {
    const transactions = builderData.sourceTransactions
      .filter((transaction) => transaction.data.totalAmount !== 0)
      .map((transaction) => this.buildPaymentTransaction(transaction));
    const refunds = builderData.sourceRefunds
      .filter((transaction) => transaction.data.totalAmount !== 0)
      .map((refund) => this.buildPaymentTransaction(refund));
    const discounts = builderData.sourceDiscounts
      .filter((transaction) => transaction.data.totalAmount !== 0)
      .map((discount) => this.buildDiscountTransaction(discount));

    this.accountSummary = {
      ...builderData,
      transactions,
      refunds,
      discounts,
    };
  }

  validateAccountSummary(): void {
    const calculatedBalance = [
      ...this.accountSummary.sourceTransactions,
      ...this.accountSummary.sourceLineItems,
      ...this.accountSummary.sourceRefunds,
      ...this.accountSummary.sourceDiscounts,
    ].reduce((acc, record) => acc + record.data.totalAmount, 0);

    if (calculatedBalance !== this.accountSummary.balance) {
      throw new Error(
        `Account balance mismatch. Calculated: ${calculatedBalance}, Expected: ${this.accountSummary.balance}`
      );
    }
  }

  buildPaymentTransaction(
    transaction: IGetRecordResponse<PracticeWorksTransaction>
  ): PractitionerTransaction {
    const staffer = getPractitionerOrDefault(
      transaction.data.producerStaffId.toString(),
      this.staff,
      this.practitioners
    );
    if (!staffer) {
      throw new Error(
        `Staffer not found for ID: ${transaction.data.producerStaffId}`
      );
    }

    const transactionAmount = transaction.data.totalAmount;
    const createdAt = toTimestamp(
      toMomentTz(
        transaction.data.createdDate,
        this.migration.configuration.timezone
      )
    );

    const mappedPaymentType = this.transactionTypes.find(
      (paymentType) =>
        paymentType.sourceIdentifier ===
        transaction.data.treatmentCodeId.toString()
    );
    const provider =
      mappedPaymentType?.destinationValue?.value ?? TransactionProvider.Manual;
    const providerSubType = isINamedDocument<IManualTransactionType>(
      mappedPaymentType?.associatedValue
    )
      ? mappedPaymentType?.associatedValue
      : undefined;
    const extendedData: IManualExtendedData = buildExtendedData(
      transaction,
      provider,
      providerSubType
    );

    return {
      ...Transaction.init({
        uid: transaction.data.transactionId.toString(),
        reference: transaction.data.transactionId.toString(),
        provider,
        from: '',
        to: '',
        amount: Money.amount(Math.abs(transactionAmount)),
        type:
          transactionAmount > 0
            ? TransactionType.Outgoing
            : TransactionType.Incoming,
        description: transaction.data.serviceDescription,
        createdAt,
        extendedData,
        practiceRef: this.migration.configuration.practices[0].ref,
        status: TransactionStatus.Complete,
      }),
      practitionerRef: staffer.ref,
    };
  }

  buildDiscountTransaction(
    transaction: IGetRecordResponse<PracticeWorksTransaction>
  ): PractitionerTransaction {
    const staffer = getPractitionerOrDefault(
      transaction.data.producerStaffId.toString(),
      this.staff,
      this.practitioners
    );
    if (!staffer) {
      throw new Error(
        `Staffer not found for ID: ${transaction.data.producerStaffId}`
      );
    }

    const transactionAmount = transaction.data.totalAmount;
    const createdAt = toTimestamp(
      toMomentTz(
        transaction.data.createdDate,
        this.migration.configuration.timezone
      )
    );

    const mappedPaymentType = this.transactionTypes.find(
      (paymentType) =>
        paymentType.sourceIdentifier ===
        transaction.data.treatmentCodeId.toString()
    );
    const provider =
      mappedPaymentType?.destinationValue?.value ??
      TransactionProvider.Discount;
    const providerSubType = isINamedDocument<IManualTransactionType>(
      mappedPaymentType?.associatedValue
    )
      ? mappedPaymentType?.associatedValue
      : undefined;
    const extendedData: IDiscountExtendedData = {
      ...buildExtendedData(transaction, provider, providerSubType),
      practitionerRef: staffer.ref,
    };

    return {
      ...Transaction.init({
        uid: transaction.data.transactionId.toString(),
        reference: transaction.data.transactionId.toString(),
        provider,
        from: '',
        to: '',
        amount: Money.amount(Math.abs(transactionAmount)),
        type:
          transactionAmount > 0
            ? TransactionType.Outgoing
            : TransactionType.Incoming,
        description: transaction.data.serviceDescription,
        createdAt,
        extendedData,
        practiceRef: this.migration.configuration.practices[0].ref,
        status: TransactionStatus.Complete,
      }),
      practitionerRef: staffer.ref,
    };
  }

  async buildInvoices(
    patient: IBasePatient & IPatientContactDetails,
    practice: WithRef<IPractice>
  ): Promise<IInvoiceBuildData[]> {
    const invoices = await this.buildInvoiceData(patient, practice);
    const sortedData = invoices.sort((invoiceA, invoiceB) =>
      sortTimestampAsc(invoiceA.invoice.createdAt, invoiceB.invoice.createdAt)
    );

    let availableTransactions = [
      ...this.accountSummary.transactions,
      ...this.accountSummary.discounts,
      ...this.accountSummary.refunds,
    ].sort((transactionA, transactionB) =>
      sortTimestampAsc(transactionA.createdAt, transactionB.createdAt)
    );

    const invoicePractitioners = uniqBy(
      invoices.map((invoice) => invoice.practitionerRef),
      (practitionerRef) => practitionerRef.path
    );

    const allResults: IInvoiceBuildData[] = [];
    for (const practitionerRef of invoicePractitioners) {
      const result = this._buildPractitionerInvoices(
        sortedData,
        practitionerRef,
        availableTransactions
      );

      allResults.push(...result.invoices);
      availableTransactions = result.remainingTransactions;
    }

    if (availableTransactions.length && allResults.length) {
      const lastInvoiceIndex = allResults.length - 1;
      const lastInvoice = allResults[lastInvoiceIndex];

      const finalInvoiceData = this.buildFinalInvoice(
        lastInvoice,
        availableTransactions
      );

      lastInvoice.invoice.items.push(...finalInvoiceData.lineItems);
      lastInvoice.transactions = finalInvoiceData.transactions;
    }

    return allResults.map((invoiceData) => this.setInvoiceStatus(invoiceData));
  }

  buildFinalInvoice(
    invoiceData: IInvoiceBuildData,
    remainingTransactions: PractitionerTransaction[]
  ): {
    transactions: PractitionerTransaction[];
    lineItems: ICustomLineItem[];
  } {
    const lineItems = this.buildFinalLineItems(
      remainingTransactions,
      invoiceData
    );

    return {
      lineItems,
      transactions: [...invoiceData.transactions, ...remainingTransactions],
    };
  }

  buildFinalLineItems(
    remainingTransactions: PractitionerTransaction[],
    invoiceData: IInvoiceBuildData
  ): ICustomLineItem[] {
    const depositAmount = Money.amount(
      sum(remainingTransactions.map((payment) => getTransactionAmount(payment)))
    );

    if (depositAmount > 0) {
      return [
        {
          uid: `${invoiceData.invoice.uid}-deposit`,
          type: InvoiceLineItemType.Deposit,
          description: 'Overpayment Balance',
          amount: depositAmount,
          tax: 0,
          taxStatus: TaxStrategy.GSTApplicable,
          quantity: 1,
        },
      ];
    }

    return [];
  }

  async buildInvoiceData(
    patient: IBasePatient & IPatientContactDetails,
    practice: WithRef<IPractice>
  ): Promise<IInvoiceBuildData[]> {
    const groupedTreatments = groupBy(
      this.accountSummary.sourceLineItems,
      (sourceTreatment) =>
        `${sourceTreatment.data.serviceDate}-${sourceTreatment.data.producerStaffId}`
    );

    const invoiceData: IInvoiceBuildData[] = await resolveSequentially(
      Object.values(groupedTreatments),
      async (treatments) => {
        const sortedTreatments = sortBy(
          treatments,
          (treatment) => treatment.data.transactionId
        );
        const sourceInvoice = sortedTreatments[0];
        const appointmentDate = toMomentTz(
          sourceInvoice.data.serviceDate,
          practice.settings.timezone
        );
        const createdAt = toMomentTz(
          sourceInvoice.data.createdDate,
          practice.settings.timezone
        );
        const dueDate = toTimestamp(appointmentDate.clone().add(1, 'week'));
        const practitioner = getPractitionerOrDefault(
          sourceInvoice.data.producerStaffId.toString(),
          this.staff,
          this.practitioners
        );

        if (!practitioner) {
          throw new Error(
            `Staffer not found for ID: ${sourceInvoice.data.producerStaffId}`
          );
        }

        const invoice = Invoice.init({
          due: dueDate,
          practice: toNamedDocument(practice),
          from: toAccountDetails(practice),
          to: toAccountDetails(patient),
          issuedAt: toTimestamp(appointmentDate),
          reference: sourceInvoice.data.transactionId.toString(),
          createdAt: toTimestamp(createdAt),
        });

        const onBehalfOf = await this._getOnBehalfOf(sortedTreatments, patient);
        if (onBehalfOf) {
          invoice.to = {
            ...invoice.to,
            onBehalfOf,
          };
        }

        const lineItems = sortedTreatments.map((treatment) => {
          const staffer =
            sourceInvoice.data.providerStaffId !==
            treatment.data.producerStaffId
              ? getPractitionerOrDefault(
                  treatment.data.producerStaffId.toString(),
                  this.staff,
                  this.practitioners
                )
              : practitioner;

          if (!staffer) {
            throw new Error(
              `Staffer not found for ID: ${treatment.data.producerStaffId}`
            );
          }

          return this.getLineItems(treatment, staffer);
        });

        const invoiceWithId = {
          ...invoice,
          items: lineItems.flat(),
          uid: sourceInvoice.data.transactionId.toString(),
        };

        return {
          invoice: invoiceWithId,
          transactions: [],
          practitionerRef: practitioner.ref,
        };
      }
    );

    return invoiceData;
  }

  getLineItems(
    treatment: IGetRecordResponse<PracticeWorksTransaction>,
    staffer: INamedDocument<IStaffer>
  ): ITreatmentLineItem[] | ICustomLineItem[] {
    if (!treatment.data.itemCode) {
      throw new Error(
        `Treatment missing item code for transaction ID: ${treatment.data.transactionId}`
      );
    }

    const [code, mappedTreatment] = resolveMappedCodeTreatmentPair(
      this.sourceItemCodes,
      this.sourceTreatmentMappings,
      treatment.data.itemCode,
      treatment.data.itemCode
    );

    if (!code && !mappedTreatment) {
      // eslint-disable-next-line no-console
      console.error(
        `No mapping found for item code: ${treatment.data.itemCode} (Transaction ID: ${treatment.data.transactionId})`
      );

      return [
        {
          uid: treatment.data.transactionId.toString(),
          type: InvoiceLineItemType.Fee,
          description: `${
            treatment.data.displayCode ?? treatment.data.itemCode
          } ${treatment.data.serviceDescription}`,
          amount: Money.amount(treatment.data.totalAmount),
          tax: 0,
          taxStatus: TaxStrategy.GSTApplicable,
          quantity: 1,
        },
      ];
    }

    const treatmentRef = {
      treatmentUuid: treatment.data.transactionId.toString(),
      attributedTo: staffer,
    };

    if (code) {
      const description = `${code.code.toString()} - ${
        treatment.data.serviceDescription
      }`;
      const serviceCodeItem = {
        uid: treatment.data.transactionId.toString(),
        type: InvoiceLineItemType.ServiceCode,
        code: code.code.toString(),
        // toothId: treatment.data.t
        description: treatment.data.displayCode
          ? `${description} (${treatment.data.displayCode})`
          : description,
        amount: Money.amount(treatment.data.totalAmount),
        tax: 0,
        taxStatus: TaxStrategy.GSTApplicable,
        quantity: 1,
      };

      return [
        {
          uid: treatment.data.transactionId.toString(),
          type: InvoiceLineItemType.Treatment,
          treatmentRef,
          items: [serviceCodeItem],
          description,
          amount: Money.amount(treatment.data.totalAmount),
          tax: 0,
          taxStatus: TaxStrategy.GSTApplicable,
          quantity: 1,
        },
      ];
    }

    const description = `${
      treatment.data.displayCode ?? treatment.data.itemCode
    } ${treatment.data.serviceDescription}`;

    return [
      {
        uid: treatment.data.transactionId.toString(),
        type: InvoiceLineItemType.Treatment,
        treatmentRef,
        items: [
          {
            uid: treatment.data.transactionId.toString(),
            type: InvoiceLineItemType.TreatmentBasePrice,
            treatmentRef,
            description: '',
            amount: Money.amount(treatment.data.totalAmount),
            tax: 0,
            taxStatus: TaxStrategy.GSTApplicable,
            quantity: 1,
          },
        ],
        description,
        amount: Money.amount(treatment.data.totalAmount),
        tax: 0,
        taxStatus: TaxStrategy.GSTApplicable,
        quantity: 1,
      },
    ];
  }

  getInvoiceStatus(amountRemaining: number): InvoiceStatus {
    if (amountRemaining <= 0) {
      return InvoiceStatus.Paid;
    }

    if (amountRemaining > 0) {
      return InvoiceStatus.Issued;
    }

    return InvoiceStatus.Draft;
  }

  setInvoiceStatus(invoiceData: IInvoiceBuildData): IInvoiceBuildData {
    const status = this.getInvoiceStatus(
      Invoice.balance(invoiceData.invoice, invoiceData.transactions)
    );
    const lastTransaction = new TransactionOperators(invoiceData.transactions)
      .completed()
      .sort(sortByCreatedAt)
      .reverse()
      .last();

    if (status === InvoiceStatus.Paid && lastTransaction) {
      invoiceData.invoice.paidAt = lastTransaction.createdAt;
    }
    Invoice.updateStatus(
      invoiceData.invoice,
      status,
      invoiceData.invoice.createdAt
    );

    return invoiceData;
  }

  private async _getOnBehalfOf(
    sortedTreatments: IGetRecordResponse<PracticeWorksTransaction>[],
    patient: IBasePatient & IPatientContactDetails
  ): Promise<IAccountDetails | undefined> {
    const isOnBehalfOf = sortedTreatments.find(
      (treatment) =>
        treatment.data.patientPersonId !== 0 &&
        treatment.data.responsePatientId !== treatment.data.patientPersonId
    );
    if (!isOnBehalfOf) {
      return;
    }
    const onBehalfOfRef = await this.translationMap.getDestination<IPatient>(
      isOnBehalfOf.data.patientPersonId.toString(),
      PATIENT_RESOURCE_TYPE
    );

    if (!onBehalfOfRef) {
      throw new Error(
        `Couldn't resolve on behalf of patient for ID: ${isOnBehalfOf.data.patientPersonId}`
      );
    }
    return toAccountDetails(patient);
  }

  private _buildPractitionerInvoices(
    invoiceBuildData: IInvoiceBuildData[],
    practitionerRef: DocumentReference<IStaffer>,
    transactions: PractitionerTransaction[]
  ): IInvoiceDataWithRemainder {
    const practitionerInvoices = invoiceBuildData.filter((invoiceData) =>
      isSameRef(invoiceData.practitionerRef, practitionerRef)
    );

    if (practitionerInvoices.length === 0) {
      return {
        invoices: [],
        remainingTransactions: transactions,
      };
    }

    const practitionerTransactions = transactions.filter((transaction) =>
      isSameRef(transaction.practitionerRef, practitionerRef)
    );
    const otherTransactions = transactions.filter(
      (transaction) => !isSameRef(transaction.practitionerRef, practitionerRef)
    );

    const prioritizedTransactions = [
      ...practitionerTransactions,
      ...otherTransactions,
    ].sort((transactionA, transactionB) =>
      sortTimestampAsc(transactionA.createdAt, transactionB.createdAt)
    );

    const invoicesResult = this._buildInvoiceDataWithRemainder(
      practitionerInvoices,
      prioritizedTransactions
    );

    return {
      invoices: invoicesResult.invoices,
      remainingTransactions: invoicesResult.remainingTransactions,
    };
  }

  private _buildInvoiceDataWithRemainder(
    invoiceBuildData: IInvoiceBuildData[],
    transactions: PractitionerTransaction[]
  ): IInvoiceDataWithRemainder {
    const results: IInvoiceBuildData[] = [];
    let remainingTransactions = [...transactions];

    invoiceBuildData.forEach((invoiceData, index) => {
      const [transactionsForInvoice, transactionsAfter] =
        getTransactionsForBalance(remainingTransactions, invoiceData);
      remainingTransactions = transactionsAfter;

      const isLastInvoice = index === invoiceBuildData.length - 1;
      if (!isLastInvoice) {
        results.push({
          invoice: invoiceData.invoice,
          transactions: [
            ...invoiceData.transactions,
            ...transactionsForInvoice,
          ],
          practitionerRef: invoiceData.practitionerRef,
        });
        return;
      }

      results.push({
        invoice: invoiceData.invoice,
        transactions: [...invoiceData.transactions, ...transactionsForInvoice],
        practitionerRef: invoiceData.practitionerRef,
      });
    });

    return {
      invoices: results,
      remainingTransactions,
    };
  }
}
